'use client';

import { useState, useMemo } from 'react';
import { Drama, Check, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useMasquerade } from '@/hooks/useMasquerade';
import { RoleIcon } from '@/components/access-control/roles/RoleIcon';
import type { Role } from '@/lib/access-control/types';
import { getPrivilegeBadge } from '@/lib/access-control/masquerade-ui';

interface MasqueradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MasqueradeDialog({ open, onOpenChange }: MasqueradeDialogProps) {
  const {
    availableRoles,
    activateMasquerade,
    loading,
  } = useMasquerade();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleRole = (roleId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
    setError(null);
  };

  // Compute union of selected roles' permission counts
  const selectedRoles = useMemo(
    () => availableRoles.filter(r => selectedIds.has(r.id)),
    [availableRoles, selectedIds]
  );

  const handleActivate = async () => {
    if (selectedIds.size === 0) return;

    try {
      setError(null);
      await activateMasquerade([...selectedIds]);
      setSelectedIds(new Set());
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate masquerade');
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedIds(new Set());
      setError(null);
      setShowPreview(false);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg bg-zinc-950 border-blue-500/20">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Drama className="w-5 h-5 text-amber-400" />
            Masquerade as Role
          </DialogTitle>
          <DialogDescription className="text-white/70">
            Temporarily assume different platform roles to test permissions. Scoped tenant and org masquerade is not supported yet.
          </DialogDescription>
        </DialogHeader>

        {/* Role List */}
        <div className="max-h-80 overflow-y-auto space-y-1.5 py-2">
          {availableRoles.length === 0 ? (
            <p className="text-sm text-white/70 text-center py-6">
              No platform roles are available for your current privilege level.
            </p>
          ) : (
            availableRoles.map((role) => {
              const isSelected = selectedIds.has(role.id);
              const badge = getPrivilegeBadge(role.privilege_level);

              return (
                <button
                  key={role.id}
                  onClick={() => toggleRole(role.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isSelected
                      ? 'bg-amber-500/10 border border-amber-500/30'
                      : 'bg-white/[0.02] border border-transparent hover:bg-white/[0.05]'
                  }`}
                >
                  {/* Checkbox */}
                  <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isSelected
                      ? 'bg-amber-500 border-amber-500'
                      : 'border-white/20 bg-transparent'
                  }`}>
                    {isSelected && <Check className="w-3.5 h-3.5 text-black" />}
                  </div>

                  {/* Role Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white/90">{role.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.color}`}>
                        {badge.label} ({role.privilege_level})
                      </span>
                    </div>
                    {role.description && (
                      <p className="text-xs text-white/70 mt-0.5 truncate">{role.description}</p>
                    )}
                  </div>

                  {/* Role icon */}
                  <RoleIcon badgeUrl={role.badge_url} color={role.color || '#ffffff33'} size="sm" className="shrink-0" />
                </button>
              );
            })
          )}
        </div>

        {/* Preview Section */}
        {selectedRoles.length > 0 && (
          <div className="border-t border-white/[0.06] pt-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white/70 transition-colors"
            >
              {showPreview ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Preview: {selectedRoles.length} role{selectedRoles.length > 1 ? 's' : ''} selected
              {selectedRoles.length > 1 && ' (permissions stacked)'}
            </button>

            {showPreview && (
              <div className="mt-2 space-y-1">
                {selectedRoles.map(role => (
                  <div key={role.id} className="flex items-center gap-2 text-xs text-white/60 px-2 py-1 bg-white/[0.02] rounded">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: role.color || '#6366f1' }}
                    />
                    {role.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-sm text-red-400 px-1">{error}</p>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={() => handleClose(false)}
            className="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleActivate}
            disabled={selectedIds.size === 0 || loading}
            className="px-4 py-2 text-sm font-medium text-black bg-amber-500 hover:bg-amber-400 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Activating...' : 'Activate Masquerade'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
